import React from 'react';
import { KeyboardAvoidingView, Modal as RNModal, Platform, Pressable, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useBreakpoint } from '../lib/hooks';
import { IconButton } from './Button';
import { Text } from './Text';
import { radius, useTheme } from './theme';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  /** dialog width on tablets / desktop */
  width?: number;
  /** don't wrap children in a ScrollView (children manage their own scrolling) */
  noScroll?: boolean;
  testID?: string;
}

/**
 * Modal that is a bottom sheet on phones and a centred dialog on wider screens.
 * Escape / Android back / backdrop press → onClose.
 */
export function Modal({ open, onClose, title, subtitle, children, footer, width = 520, noScroll, testID }: SheetProps) {
  const { colors } = useTheme();
  const { isPhone, height } = useBreakpoint();
  const insets = useSafeAreaInsets();
  const sheet = isPhone;
  return (
    <RNModal visible={open} transparent animationType={sheet ? 'slide' : 'fade'} onRequestClose={onClose} statusBarTranslucent>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1 }}>
        <View style={{ flex: 1, justifyContent: sheet ? 'flex-end' : 'center', alignItems: 'center', padding: sheet ? 0 : 24 }}>
          <Pressable accessibilityLabel="Close dialog" onPress={onClose} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: colors.overlay }} />
          <View
            testID={testID}
            accessibilityViewIsModal
            style={{
              width: sheet ? '100%' : '100%',
              maxWidth: sheet ? undefined : width,
              maxHeight: sheet ? height * 0.92 : height - 48,
              backgroundColor: colors.surface,
              borderRadius: radius.xl,
              borderBottomLeftRadius: sheet ? 0 : radius.xl,
              borderBottomRightRadius: sheet ? 0 : radius.xl,
              borderWidth: 1,
              borderColor: colors.border,
              overflow: 'hidden',
              paddingBottom: sheet ? insets.bottom : 0,
            }}
          >
            {(title || subtitle) && (
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', paddingLeft: 18, paddingRight: 8, paddingTop: 12, paddingBottom: 8, gap: 8, borderBottomWidth: 1, borderBottomColor: colors.border }}>
                <View style={{ flex: 1, paddingTop: 6 }}>
                  {!!title && <Text variant="h3">{title}</Text>}
                  {!!subtitle && <Text variant="small">{subtitle}</Text>}
                </View>
                <IconButton icon="x" label="Close" onPress={onClose} />
              </View>
            )}
            {noScroll ? (
              <View style={{ flexShrink: 1 }}>{children}</View>
            ) : (
              <ScrollView style={{ flexShrink: 1 }} contentContainerStyle={{ padding: 18, gap: 14 }} keyboardShouldPersistTaps="handled">
                {children}
              </ScrollView>
            )}
            {footer && (
              <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8, padding: 12, borderTopWidth: 1, borderTopColor: colors.border, flexWrap: 'wrap' }}>{footer}</View>
            )}
          </View>
        </View>
      </KeyboardAvoidingView>
    </RNModal>
  );
}

export const Sheet = Modal;
